// Conversation List pane for Inbox v2 (PRD §1.7, WP-04).
// Multi-number switcher, reload, options (Order by: Recent Reply vs Communication; Bot Reply Status: Replying vs Paused),
// search, initials avatar, media prefixes, last-sender badge, label pills, unread count badge.
import { useMemo, useState, useRef, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import { ALL_ACCOUNTS, numberLabel, useAccounts } from '../../lib/accounts';
import {
  conversationMatchesFilter,
  type ConversationFolder,
  type ConversationSortOrder,
  type BotReplyFilter,
  type LiveConversation,
} from '../../lib/inbox';
import {
  SearchIcon,
  RefreshIcon,
  MoreVerticalIcon,
  StarIcon,
} from '../icons';

interface ConversationListProps {
  conversations: LiveConversation[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  folder: ConversationFolder;
  onReload: () => void;
}

function ChannelDot({ channel }: { channel: LiveConversation['channel'] }) {
  return (
    <span
      className={cn(
        'inline-block h-2 w-2 rounded-full shrink-0',
        channel === 'messenger' ? 'bg-blue-500' : 'bg-emerald-500',
      )}
      aria-hidden
    />
  );
}

function formatPreviewText(c: LiveConversation): string {
  const p = c.preview || '';
  if (p.includes('[media]')) return '📷 Photo';
  return p;
}

function getFolderTitle(folder: ConversationFolder, t: (k: string) => string): string {
  switch (folder) {
    case 'mine':
      return t('inbox.folders.mine');
    case 'all':
      return t('inbox.folders.all');
    case 'starred':
      return t('inbox.folders.starred');
    case 'unread':
      return t('inbox.folders.unread');
    case 'archived':
      return t('inbox.folders.archived');
    case 'blocked':
      return t('inbox.folders.blocked');
    case 'resolved':
      return t('inbox.folders.resolved');
    default:
      return folder;
  }
}

function getBotStatusTitle(bs: BotReplyFilter, t: (k: string) => string): string {
  switch (bs) {
    case 'all':
      return t('inbox.options.bot.all');
    case 'replying':
      return t('inbox.options.bot.replying');
    case 'paused':
      return t('inbox.options.bot.paused');
    default:
      return bs;
  }
}

export default function ConversationList({
  conversations,
  selectedId,
  folder,
  onSelect,
  onReload,
}: ConversationListProps) {
  const { t } = useI18n();
  const { numbers, selectedId: selectedAccountId } = useAccounts();
  const [query, setQuery] = useState('');
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [orderBy, setOrderBy] = useState<ConversationSortOrder>('recent_reply');
  const [botStatus, setBotStatus] = useState<BotReplyFilter>('all');
  const optionsRef = useRef<HTMLDivElement>(null);

  // Close options menu on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!optionsRef.current?.contains(e.target as Node)) {
        setOptionsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const multi = numbers.length > 1;
  const labelFor = (id: string) => numberLabel(numbers.find((n) => n.id === id)) || id;

  const filteredAndSortedList = useMemo(() => {
    const list = conversations.filter((c) => {
      return conversationMatchesFilter(c, {
        folder,
        botStatus,
        sortBy: orderBy,
        query,
        integrationId: selectedAccountId === ALL_ACCOUNTS ? undefined : selectedAccountId,
      });
    });

    // Sort order
    return list.sort((a, b) => {
      if (orderBy === 'recent_reply') {
        const timeA = a.lastInboundAt ?? a.lastMessageAt ?? a.updatedAt ?? 0;
        const timeB = b.lastInboundAt ?? b.lastMessageAt ?? b.updatedAt ?? 0;
        return timeB - timeA;
      } else {
        const timeA = a.updatedAt ?? a.lastMessageAt ?? 0;
        const timeB = b.updatedAt ?? b.lastMessageAt ?? 0;
        return timeB - timeA;
      }
    });
  }, [conversations, folder, botStatus, orderBy, query, selectedAccountId]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-white dark:bg-slate-900 border-e border-line dark:border-slate-800 transition-colors">
      {/* Top Search & Filter Bar */}
      <div className="border-b border-line p-3 space-y-2.5 dark:border-slate-800">
        {/* Controls row */}
        <div className="flex items-center justify-between gap-1.5">
          <span className="text-xs font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400 capitalize">
            {getFolderTitle(folder, t) || folder} ({filteredAndSortedList.length})
          </span>

          <div className="flex items-center gap-1">
            {/* Reload button */}
            <button
              type="button"
              onClick={onReload}
              title={t('common.reload') || 'Reload'}
              className="grid h-7 w-7 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors"
            >
              <RefreshIcon size={14} />
            </button>

            {/* Options Dropdown */}
            <div className="relative" ref={optionsRef}>
              <button
                type="button"
                onClick={() => setOptionsOpen((v) => !v)}
                title={t('inbox.optionsTitle') || 'Conversation options'}
                className={cn(
                  'grid h-7 w-7 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors',
                  (optionsOpen || botStatus !== 'all' || orderBy !== 'recent_reply') &&
                    'bg-slate-100 text-brand dark:bg-slate-800',
                )}
              >
                <MoreVerticalIcon size={14} />
              </button>

              {optionsOpen && (
                <div className="absolute end-0 top-full z-40 mt-1 w-60 overflow-hidden rounded-xl border border-line bg-white p-3 shadow-2xl dark:border-slate-800 dark:bg-slate-900 space-y-3">
                  {/* Order By */}
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-ink-4 dark:text-slate-500 mb-1">
                      {t('inbox.options.orderBy') || 'Order By'}
                    </label>
                    <div className="space-y-1">
                      <button
                        type="button"
                        onClick={() => setOrderBy('recent_reply')}
                        className={cn(
                          'w-full text-start px-2 py-1 rounded text-xs transition-colors',
                          orderBy === 'recent_reply'
                            ? 'bg-brand-50 text-brand font-bold dark:bg-brand-950/40 dark:text-brand-tint'
                            : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800',
                        )}
                      >
                        {t('inbox.options.recentReply') || 'Subscriber Recent Reply'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setOrderBy('recent_communication')}
                        className={cn(
                          'w-full text-start px-2 py-1 rounded text-xs transition-colors',
                          orderBy === 'recent_communication'
                            ? 'bg-brand-50 text-brand font-bold dark:bg-brand-950/40 dark:text-brand-tint'
                            : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800',
                        )}
                      >
                        {t('inbox.options.recentCommunication') || 'Recent Communication'}
                      </button>
                    </div>
                  </div>

                  {/* Bot Reply Status Filter */}
                  <div className="border-t border-line pt-2.5 dark:border-slate-800">
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-ink-4 dark:text-slate-500 mb-1">
                      {t('inbox.options.botStatus') || 'Bot Reply Status'}
                    </label>
                    <div className="space-y-1">
                      {(['all', 'replying', 'paused'] as BotReplyFilter[]).map((bs) => (
                        <button
                          key={bs}
                          type="button"
                          onClick={() => setBotStatus(bs)}
                          className={cn(
                            'w-full text-start px-2 py-1 rounded text-xs capitalize transition-colors',
                            botStatus === bs
                              ? 'bg-brand-50 text-brand font-bold dark:bg-brand-950/40 dark:text-brand-tint'
                              : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800',
                          )}
                        >
                          {getBotStatusTitle(bs, t) || bs}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search input */}
        <div className="relative">
          <SearchIcon
            size={14}
            className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('inbox.searchPlaceholder') || 'Search conversations…'}
            className="w-full rounded-xl border border-line bg-surface py-2 ps-8 pe-3 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-100"
          />
        </div>
      </div>

      {/* Conversations List */}
      <div className="min-h-0 flex-1 overflow-y-auto divide-y divide-line dark:divide-slate-800">
        {filteredAndSortedList.length === 0 ? (
          <div className="p-8 text-center text-xs text-ink-3 dark:text-slate-500">
            {t('inbox.emptyList') || 'No conversations found'}
          </div>
        ) : (
          filteredAndSortedList.map((c) => {
            const isSelected = c.id === selectedId;
            const previewText = formatPreviewText(c);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => onSelect(c.id)}
                className={cn(
                  'flex w-full items-start gap-3 p-3.5 text-start transition-colors',
                  isSelected
                    ? 'bg-brand-50/70 dark:bg-brand-950/30'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/60',
                )}
              >
                {/* Contact Avatar */}
                <div className="relative shrink-0">
                  <span
                    className={cn(
                      'grid h-10 w-10 place-items-center rounded-full text-xs font-bold text-white shadow-sm',
                      c.avatarColor,
                    )}
                    aria-hidden
                  >
                    {c.initials}
                  </span>
                  {c.starred && (
                    <span className="absolute -bottom-1 -end-1 grid h-4 w-4 place-items-center rounded-full bg-white shadow dark:bg-slate-900">
                      <StarIcon size={10} className="text-amber-400 fill-amber-400" />
                    </span>
                  )}
                </div>

                {/* Details */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-1">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <ChannelDot channel={c.channel} />
                      <b className="truncate text-xs text-ink dark:text-slate-100">
                        {c.displayName || c.name}
                      </b>
                    </span>
                    <span className="shrink-0 text-[10px] text-ink-4 dark:text-slate-500">
                      {c.time}
                    </span>
                  </div>

                  <div className="mt-1 flex items-center justify-between gap-2">
                    {c.lastMessageStatus === 'failed' ? (
                      <span className="flex items-center gap-1 text-xs font-medium text-rose-600 dark:text-rose-400 truncate">
                        <span aria-hidden>⚠️</span>
                        <span className="truncate">{previewText || t('inbox.failed.notDelivered')}</span>
                      </span>
                    ) : (
                      <span className="truncate text-xs text-ink-3 dark:text-slate-400">
                        {previewText}
                      </span>
                    )}
                    {c.unread > 0 && (
                      <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-brand px-1 text-[9px] font-extrabold text-white shadow-sm">
                        {c.unread}
                      </span>
                    )}
                  </div>

                  {/* Badges Row (Last Sender, Number Account, Labels) */}
                  <div className="mt-1.5 flex flex-wrap items-center gap-1">
                    {/* Last Sender Badge */}
                    {c.lastSender && (
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.2 text-[9px] font-bold uppercase tracking-wider',
                          c.lastSender === 'user'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400'
                            : c.lastSender === 'bot'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-400'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-400',
                        )}
                      >
                        {c.lastSender}
                      </span>
                    )}

                    {/* Account switcher number label */}
                    {multi && selectedAccountId === ALL_ACCOUNTS && (
                      <span className="rounded bg-slate-100 px-1.5 py-0.2 text-[9px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300 truncate max-w-[120px]">
                        📱 {labelFor(c.integrationId ?? 'primary')}
                      </span>
                    )}

                    {/* Label pills */}
                    {c.labels.slice(0, 2).map((l) => (
                      <span
                        key={l}
                        className="rounded bg-brand-50 px-1.5 py-0.2 text-[9px] font-bold text-brand dark:bg-brand-950/40 dark:text-brand-tint"
                      >
                        {l}
                      </span>
                    ))}
                    {c.labels.length > 2 && (
                      <span className="text-[9px] font-bold text-ink-4 dark:text-slate-500">
                        +{c.labels.length - 2}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
